import { GetEventResponse } from '@orbit/api-types'
import { TripHelper } from '@lib/TripHelper'
import { TripDayInfo } from '@hooks'
import { Event, Trip } from '@types'
import { Day, Itinerary } from './types'

/**
 * Calculates the actual date from trip start date and tripDayIndex
 * @param backendEvent - Event from the backend API
 * @param trip - Trip object containing startDate
 * @returns Date string in YYYY-MM-DD format
 */
export const calculateEventDate = (backendEvent: Event, trip: Trip): string => {
	const tripHelper = new TripHelper(trip)
	return tripHelper.calculateDateFromDayIndex(backendEvent.tripDayIndex)
}

export const generateItinerary = (tripData: Trip, events?: GetEventResponse[], tripDayInfos?: TripDayInfo[]): Itinerary | null => {
	const tripHelper = new TripHelper(tripData)

	if (!tripHelper.hasValidDates()) {
		console.warn('Trip data missing startDate or dayCount')
		return null
	}

	const startDate = tripHelper.getStartDate()!
	const endDate = tripHelper.getEndDate()!

	// Format date as YYYY-MM-DD using local date to avoid UTC timezone shifts
	const formatDateToString = (date: Date): string => {
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const days: Day[] = []

	// Generate days from startDate to endDate
	for (let d = new Date(startDate), dayIndex = 0; d <= endDate; d.setDate(d.getDate() + 1), dayIndex++) {
		const displayName = d.toLocaleDateString('en-US', { weekday: 'long' })
		const dateString = formatDateToString(d)

		// Filter and sort events for this day
		const dayEvents =
			events
				?.filter(event => event.tripDayIndex === dayIndex)
				.sort((a, b) => a.eventIndex - b.eventIndex) || []

		const dayInfo = tripDayInfos?.find(info => info.dayIndex === dayIndex)

		days.push({
			id: dayIndex.toString(),
			date: dateString,
			displayName,
			events: dayEvents,
			name: dayInfo?.name ?? null,
			note: dayInfo?.note ?? null,
		})
	}

	const newItinerary: Itinerary = {
		id: `itinerary-${Date.now()}`,
		title: `${tripData.name} Itinerary`,
		startDate: formatDateToString(startDate),
		endDate: formatDateToString(endDate),
		days,
	}

	return newItinerary
}
